/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-29
 */

import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Divider,
  useTheme as useMuiTheme,
} from '@mui/material';
import { Brightness4, Brightness7 } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../contexts/ThemeContext';

/**
 * Appearance settings section component for managing theme and visual preferences.
 * @returns AppearanceSettings component
 */
const AppearanceSettings: React.FC = () => {
  const { t } = useTranslation('settings');
  const { toggleMode, isDark } = useTheme();
  const muiTheme = useMuiTheme();

  return (
    <Box>
      {/* Section Header */}
      <Box sx={{ p: 3, pb: 2 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          {t('appearance.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('appearance.description')}
        </Typography>
      </Box>

      <Divider />

      {/* Dark Mode Setting */}
      <Box sx={{ p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: 1,
                backgroundColor: muiTheme.palette.action.hover,
                color: muiTheme.palette.text.primary,
              }}
            >
              {isDark ? <Brightness7 /> : <Brightness4 />}
            </Box>
            <Box>
              <Typography variant="subtitle1" component="div">
                {t('appearance.themeMode')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isDark ? t('appearance.darkMode') : t('appearance.lightMode')}
              </Typography>
            </Box>
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={isDark}
                onChange={toggleMode}
                color="primary"
                inputProps={{
                  'aria-label': `Switch to ${isDark ? 'light' : 'dark'} mode`,
                }}
              />
            }
            label=""
            sx={{ m: 0 }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default AppearanceSettings;
